import type {HotelDetails} from '@/api/hoteldetails';
import type {SortParams} from '@/modules/common/context/QueryParams/models';
import type {
  OfferEcommerceItem,
  EventHandler,
} from '@/redux/middleware/googleTagManager/types';

import {
  OFFER_BOX_EVENT_ORIGIN_ALTERNATIVE_FLIGHT_OFFER,
  OFFER_BOX_EVENT_ORIGIN_ALTERNATIVE_FLIGHTS_BUTTON,
  OFFER_BOX_EVENT_ORIGIN_CTA_BUTTON,
  OFFER_BOX_EVENT_ORIGIN_FLIGHT_DETAILS_BUTTON,
  OFFER_BOX_EVENT_ORIGIN_OFFER_DETAILS_BUTTON,
  OFFER_BOX_EVENT_ORIGIN_UPSELLING_BUTTON,
  OFFER_BOX_EVENT_ORIGIN_UPSELLING_OFFER,
} from '@/modules/offers/components/OfferBox/models';
import {mapBestOfferType} from '@/redux/middleware/googleTagManager/utils/mapBestOfferType';
import {mapToSortListNameForOffer} from '@/redux/middleware/googleTagManager/utils/mapToSortListNameForOffer';
import {mapOfferEcommerceItem} from '@/redux/middleware/googleTagManager/utils/offerData';
import {wrappedFunctionToWaitForGtmDom} from '@/redux/middleware/googleTagManager/utils/waitForGtmDomEvent';
import {selectAlternativeFlightOffer} from '@/redux/v1/offerAlternativeFlights/reducer';
import {selectBestOffer, selectOffer} from '@/redux/v1/offers/list/reducer';
import {selectUpsellingOffer} from '@/redux/v1/offerUpselling/reducer';

type Action = {
  type: string;
  meta: {
    hotelId: string;
    offerId: string;
    origin: string;
    travelType: string;
    autoVacancy?: boolean;
    index: number;
    hotelDetails: HotelDetails;
    travellerCount: number;
    sortParams: SortParams;
  };
};

type NullEvent = {
  ecommerce: null;
};

type Event = {
  event: 'select_item';
  event_action: string;
  is_ad: '1 - sponsored' | '0';
  non_interaction: false;
  autovacancy: string;
  ecommerce: {
    items: Array<OfferEcommerceItem>;
  };
};

const mapOriginToEventAction = (origin: string) => {
  switch (origin) {
    case OFFER_BOX_EVENT_ORIGIN_FLIGHT_DETAILS_BUTTON: {
      return 'click_vacancy_check_in_flights';
    }
    case OFFER_BOX_EVENT_ORIGIN_OFFER_DETAILS_BUTTON: {
      return 'click_vacancy_check_in_details';
    }
    case OFFER_BOX_EVENT_ORIGIN_UPSELLING_BUTTON:
    case OFFER_BOX_EVENT_ORIGIN_UPSELLING_OFFER: {
      return 'click_vacancy_check_in_upselling';
    }
    case 'autovacancy':
    case OFFER_BOX_EVENT_ORIGIN_CTA_BUTTON: {
      return 'click_vacancy_check_in_list';
    }
    case OFFER_BOX_EVENT_ORIGIN_ALTERNATIVE_FLIGHTS_BUTTON:
    case OFFER_BOX_EVENT_ORIGIN_ALTERNATIVE_FLIGHT_OFFER: {
      return 'click_vacancy_check_in_altflights';
    }
    default: {
      return 'click_vacancy_check_in_unknown';
    }
  }
};

export const handler = wrappedFunctionToWaitForGtmDom<
  EventHandler<Action, [NullEvent, Event] | []>
>((action, state) => {
  const {
    hotelId,
    offerId,
    origin,
    autoVacancy = false,
    index,
    hotelDetails,
    travellerCount,
    sortParams,
  } = action.meta;

  // because reasons we don't want to track automatic upselling // alternative flights vacancies ¯\_(ツ)_/¯
  if (
    origin === OFFER_BOX_EVENT_ORIGIN_UPSELLING_OFFER ||
    origin === OFFER_BOX_EVENT_ORIGIN_ALTERNATIVE_FLIGHT_OFFER
  ) {
    return [];
  }

  let offer;

  switch (origin) {
    case OFFER_BOX_EVENT_ORIGIN_UPSELLING_OFFER: {
      offer = selectUpsellingOffer(offerId, state);
      break;
    }
    case OFFER_BOX_EVENT_ORIGIN_ALTERNATIVE_FLIGHT_OFFER: {
      offer = selectAlternativeFlightOffer(offerId, state);
      break;
    }
    default: {
      offer = selectOffer({hotelId, offerId}, state);
      break;
    }
  }

  const bestOffer = selectBestOffer({hotelId, offerId}, state);

  if (!offer) {
    return [];
  }

  return [
    {ecommerce: null},
    {
      event: 'select_item',
      event_action: mapOriginToEventAction(origin),
      non_interaction: false,
      is_ad: offer.sponsored?.clickUrl ? '1 - sponsored' : '0',
      autovacancy: autoVacancy ? '1' : '0',
      ecommerce: {
        items: [
          mapOfferEcommerceItem({
            hotelId,
            hotelData: hotelDetails,
            offer,
            travellerCount,
            index,
            itemListName: mapToSortListNameForOffer(sortParams.sortBy ?? ''),
            itemBestOfferType: bestOffer?.type
              ? mapBestOfferType(bestOffer?.type)
              : 'no best offer',
          }),
        ],
      },
    },
  ];
});
